// 存档读写出入口（架构 §5.5 / §6.1 / ADR-002 §2.4）。
// 依赖：仅 StorageAdapter（./storage）与常量（./constants）；不引入任何游戏语义。
import type { StorageAdapter } from './storage';
import { CURRENT_SCHEMA_VERSION, STORAGE_KEY, BACKUP_KEY_PREFIX } from './constants';
import type { SaveDataV1, LoadResult } from './types';
import { sanitizeV1 } from './sanitize';
import { migrate } from './migrate';

// 写入时机（GDD 02 ④-4.3「只在四个时机写」之一，由 domain 层调用，本层只是字节出口）。
export function persist(save: SaveDataV1, adapter: StorageAdapter): void {
  adapter.set(STORAGE_KEY, JSON.stringify(save));
}

// 读取判别联合：empty → futureVersion → corrupt → ok（ADR-002 §2.4 固定顺序）。
// now 可注入（默认 Date.now），便于测试与未来可注入时钟。
// 低版本（< CURRENT）内部走迁移（EP-002-S4）：v1→v2 已在 ./migrate 实现。
export function loadSave(adapter: StorageAdapter, now: () => number = Date.now): LoadResult {
  let raw: string | null;
  try {
    raw = adapter.get(STORAGE_KEY);
  } catch {
    // T10：读路径本身抛错（如被禁用）→ 存储不可用，降级内存态由调用方决定
    return { kind: 'storageUnavailable' };
  }

  if (raw === null) {
    return { kind: 'empty' }; // T12 首次进入
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // T9：非法 JSON → 先备份原字节，再判 corrupt
    const backupKey = writeBackup(adapter, raw, now);
    return { kind: 'corrupt', backupKey, reason: '存档不是合法 JSON' };
  }

  const version = readSchemaVersion(parsed);
  if (version === null) {
    // 缺 schemaVersion 视为损坏（无法判定版本 → 备份后重置）
    const backupKey = writeBackup(adapter, raw, now);
    return { kind: 'corrupt', backupKey, reason: '存档缺少 schemaVersion' };
  }

  if (version > CURRENT_SCHEMA_VERSION) {
    // T8：高版本只读，字节不变，绝不写回（E2）
    return { kind: 'futureVersion', found: version, current: CURRENT_SCHEMA_VERSION };
  }

  // version < CURRENT：迁移链 v1→v2（mailbox 字段引入）
  let migrated: unknown = parsed;
  if (version < CURRENT_SCHEMA_VERSION) {
    try {
      migrated = migrate(parsed, version, CURRENT_SCHEMA_VERSION);
    } catch (e) {
      const reason = e instanceof Error ? e.message : 'unknown';
      const backupKey = writeBackup(adapter, raw, now);
      return { kind: 'corrupt', backupKey, reason: `存档迁移失败：${reason}` };
    }
  }

  // ok 分支「读到合法对象后」再做一次结构校验（EP-002-S3）：结构非法则视为 corrupt 备份重置。
  const sanitized = sanitizeV1(migrated);
  if (sanitized === null) {
    const backupKey = writeBackup(adapter, raw, now);
    return { kind: 'corrupt', backupKey, reason: '存档结构校验未通过' };
  }
  return { kind: 'ok', save: sanitized };
}

function writeBackup(adapter: StorageAdapter, raw: string, now: () => number): string | null {
  const backupKey = `${BACKUP_KEY_PREFIX}${now()}`;
  try {
    adapter.set(backupKey, raw);
    return backupKey;
  } catch {
    // 备份失败也不阻塞加载流程：仍以 corrupt 返回，备份键记为 null
    return null;
  }
}

function readSchemaVersion(value: unknown): number | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = (value as Record<string, unknown>).schemaVersion;
  return typeof v === 'number' ? v : null;
}
